import { useMemo } from 'react';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { NaceDetailArray, AffinityDto } from 'models/dtoTypes';
import DoNoSignificantHarmNaceDetailBox from './_doNoSignificantHarmNaceDetailBox';
import SubSection from './_subSection';
import Empty from './_empty';
import { smartGovernancePrimaryColor } from 'kosmosConfig';
import { dnshSvg } from 'kosmos/images';
import { useTranslation } from 'react-i18next';

const PREFIX = 'DoNoSignificantHarmDetailReport';

const classes = {
  item: `${PREFIX}-item`,
};

const Root = styled('div')(() => ({
  paddingTop: 20,
  [`& .${classes.item}`]: {
    width: '100%',
  },
}));

interface IProps {
  name?: string;
  doNoSignificantHarmDetailReport: NaceDetailArray[];
  doNoSignificantHarmDetailReportSmartGovernance?: NaceDetailArray[];
}

export default function DoNoSignificantHarmDetailReport(props: IProps): JSX.Element {
  const { t } = useTranslation('default');
  const { doNoSignificantHarmDetailReport, doNoSignificantHarmDetailReportSmartGovernance = [] } =
    props;

  const filterByAffinity = (list: NaceDetailArray[], affinity: AffinityDto): NaceDetailArray[] => {
    if (list && list.length > 0) {
      return list.filter((item) => item.overallAffinity === affinity && item.affinityDescriptor);
    } else {
      return [];
    }
  };

  const positiveList = useMemo<NaceDetailArray[]>(() => {
    return filterByAffinity(doNoSignificantHarmDetailReport, 'positive');
  }, doNoSignificantHarmDetailReport);

  const negativeList = useMemo<NaceDetailArray[]>(() => {
    return filterByAffinity(doNoSignificantHarmDetailReport, 'negative');
  }, doNoSignificantHarmDetailReport);

  const warningList = useMemo<NaceDetailArray[]>(() => {
    return filterByAffinity(doNoSignificantHarmDetailReport, 'warning');
  }, doNoSignificantHarmDetailReport);

  const neutralList = useMemo<NaceDetailArray[]>(() => {
    return filterByAffinity(doNoSignificantHarmDetailReport, 'neutral');
  }, doNoSignificantHarmDetailReport);

  // const positiveSgList = useMemo<NaceDetailArray[]>(() => {
  //   return filterByAffinity(doNoSignificantHarmDetailReportSmartGovernance, 'positive');
  // }, doNoSignificantHarmDetailReport);

  // const negativeSgList = useMemo<NaceDetailArray[]>(() => {
  //   return filterByAffinity(doNoSignificantHarmDetailReportSmartGovernance, 'negative');
  // }, doNoSignificantHarmDetailReport);

  // const warningSgList = useMemo<NaceDetailArray[]>(() => {
  //   return filterByAffinity(doNoSignificantHarmDetailReportSmartGovernance, 'warning');
  // }, doNoSignificantHarmDetailReport);

  // const neutralSgList = useMemo<NaceDetailArray[]>(() => {
  //   return filterByAffinity(doNoSignificantHarmDetailReportSmartGovernance, 'neutral');
  // }, doNoSignificantHarmDetailReport);

  const sgList = useMemo<NaceDetailArray[]>(() => {
    return doNoSignificantHarmDetailReportSmartGovernance.filter(
      (item) => item.isSubstantialContribution,
    );
  }, doNoSignificantHarmDetailReportSmartGovernance);

  const renderTitle = (title: string) => {
    return (
      <>
        <Typography
          variant="h6"
          gutterBottom
          sx={{
            display: 'inline',
          }}
        >
          {title}.&nbsp;
        </Typography>
        <Typography
          variant="h6"
          gutterBottom
          sx={{
            color: smartGovernancePrimaryColor,
            display: 'inline',
          }}
        >
          {t('smart-governance-in-blue')}
        </Typography>
      </>
    );
  };
  const renderSection = (title: string, auto: NaceDetailArray[], sg: NaceDetailArray[]) => {
    if (auto.length === 0 && sg.length === 0) {
      return <></>;
    } else {
      return (
        <>
          {renderTitle(title)}
          {auto.map((item, index) => (
            <div key={index} className={classes.item}>
              <DoNoSignificantHarmNaceDetailBox
                key={index}
                naceDetailArray={item}
                isSmartGovernance={false}
              />
            </div>
          ))}
          {sg.map((item, index) => (
            <div key={index} className={classes.item}>
              <DoNoSignificantHarmNaceDetailBox
                key={index}
                naceDetailArray={item}
                isSmartGovernance={true}
              />
            </div>
          ))}
        </>
      );
    }
  };

  const renderContent = () => {
    if (
      positiveList.length === 0 &&
      negativeList.length === 0 &&
      warningList.length === 0 &&
      neutralList.length === 0 &&
      sgList.length === 0
    ) {
      return <Empty title={t('there-is-no-data-available')} />;
    }

    return (
      <Root>
        {renderSection(t('positive-contributions'), positiveList, [])}
        {renderSection(t('negative-contributions'), negativeList, [])}
        {renderSection(t('ambiguous-contributions'), warningList, [])}
        {renderSection(t('neutral-contributions'), neutralList, [])}
        {renderSection(t('substantial-contributions'), [], sgList)}
      </Root>
    );
  };

  return (
    <SubSection
      title={t('eu-taxonomy-regulation-do-no-significant-harm-dnsh-score-in-detail')}
      icon={<img src={dnshSvg} />}
    >
      {renderContent()}
    </SubSection>
  );
}
